import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { getAvatarProps } from '../../../../models/AvatarUtils';
import { getDisplayName } from '../../../../models/NameUtils';
import { Avatar } from '../../../components-v2/Avatar';
import { Button } from '../../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../components-v2/Dropdown';
import { Input } from '../../../components-v2/Input';
import { THEME } from '../../../components-v2/Theme/theme';
import { useTeammateSearch } from '../hooks/useTeammateSearch';

interface IProps {
	assignee: Api.IUser | undefined;
	onChange: (user: Api.IUser) => void;
	disabled?: boolean;
	showName?: boolean;
	label?: string;
}

export const AssigneeMenu: React.FC<IProps> = ({ assignee, onChange, disabled, showName, label }) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [filter, setFilter] = React.useState('');
	const { users, hasQuery, isLoading, isError } = useTeammateSearch({ fragment: filter, enabled: isOpen });
	const name = assignee ? getDisplayName(assignee) : 'Unassigned';

	return (
		<Dropdown
			onOpenChange={open => {
				setIsOpen(open);
				if (!open) {
					setFilter('');
				}
			}}
		>
			<DropdownTrigger
				render={props => (
					<Button
						{...props}
						variant='ghost'
						size={showName || label ? 'sm' : 'icon'}
						disabled={disabled}
						aria-label={`Assigned to ${name}. Reassign`}
						styles={[styleSheet.trigger]}
						onClick={event => {
							event.stopPropagation();
							props.onClick?.(event);
						}}
					>
						{label ? (
							<span>{label}</span>
						) : (
							<>
								<Avatar styles={[styleSheet.avatar]} smallText {...getAvatarProps(assignee)} />
								{showName ? <span>{name}</span> : null}
							</>
						)}
					</Button>
				)}
			/>
			<DropdownContent align='end' styles={[styleSheet.content]} onClick={event => event.stopPropagation()}>
				<div className={css(styleSheet.filterRow)}>
					<Input
						autoFocus
						aria-label='Find a teammate'
						placeholder='Find a teammate…'
						value={filter}
						onChange={event => setFilter(event.currentTarget.value)}
						onKeyDown={event => event.stopPropagation()}
					/>
				</div>
				<div className={css(styleSheet.list)}>
					{users.map(user => (
						<DropdownItem key={user.id} onClick={() => onChange(user)}>
							<span className={css(styleSheet.item, user.id === assignee?.id && styleSheet.selected)}>
								<Avatar styles={[styleSheet.avatar]} smallText {...getAvatarProps(user)} />
								{getDisplayName(user)}
							</span>
						</DropdownItem>
					))}
					{!hasQuery ? <span className={css(styleSheet.emptyText)}>Type a name to find a teammate</span> : null}
					{isLoading ? <span className={css(styleSheet.emptyText)}>Searching teammates…</span> : null}
					{isError ? <span className={css(styleSheet.emptyText)}>Couldn’t load teammates</span> : null}
					{hasQuery && !isLoading && !isError && users.length === 0 ? (
						<span className={css(styleSheet.emptyText)}>No teammates match</span>
					) : null}
				</div>
			</DropdownContent>
		</Dropdown>
	);
};

const styleSheet = StyleSheet.create({
	trigger: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.light,
	},
	avatar: {
		height: 22,
		width: 22,
		flexShrink: 0,
	},
	content: {
		minWidth: 220,
	},
	filterRow: {
		padding: THEME.spacing.xs,
	},
	list: {
		maxHeight: 280,
		overflowY: 'auto',
	},
	item: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	selected: {
		fontWeight: THEME.typography.fontWeights.bold,
	},
	emptyText: {
		display: 'block',
		padding: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
});
